import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Search, X, Plus, Trash2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import ListingImage from '../components/ListingImage';
import { getFirstImage } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Field options for condition builder
const PRODUCT_FIELDS = [
  { value: 'title', label: 'Title', type: 'text' },
  { value: 'type', label: 'Type', type: 'text' },
  { value: 'category', label: 'Category', type: 'text' },
  { value: 'sub_category', label: 'Sub-category', type: 'text' },
  { value: 'vendor_id', label: 'Vendor', type: 'text' },
  { value: 'tags', label: 'Tag', type: 'array' },
  { value: 'price', label: 'Price', type: 'number' },
  { value: 'compare_at_price', label: 'Compare-at price', type: 'number' },
  { value: 'status', label: 'Status', type: 'text' },
];

// Operators based on field type
const OPERATORS = {
  text: [
    { value: 'equals', label: 'is equal to' },
    { value: 'contains', label: 'contains' },
  ],
  array: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'is equal to' },
  ],
  number: [
    { value: 'equals', label: 'is equal to' },
    { value: 'greater_than', label: 'is greater than' },
    { value: 'less_than', label: 'is less than' },
  ],
};

const CollectionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    handle: '',
    collection_type: 'manual',
    listing_ids: [],
    rules: [],
    conditions_match: 'all',
    is_visible: true,
    sort_order: 'manual',
    seo_title: '',
    seo_description: ''
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchCollection();
    }
    fetchProducts();
  }, [id]);

  // Fetch matched products when rules change (for automated collections)
  useEffect(() => {
    if (formData.collection_type === 'automated' && formData.rules.length > 0) {
      fetchMatchedProducts();
    } else {
      setMatchedProducts([]);
    }
  }, [formData.rules, formData.conditions_match, formData.collection_type]);

  const fetchCollection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/collections/${id}?include_listings=true`);
      setFormData(response.data);
      if (response.data.listings) {
        setSelectedProducts(response.data.listings);
      }
    } catch (error) {
      console.error('Error fetching collection:', error);
      toast.error('Failed to load collection');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/`);
      setAvailableProducts(response.data.filter(p => p.status === 'active'));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a collection title');
      return;
    }

    if (formData.collection_type === 'automated' && formData.rules.length === 0) {
      toast.error('Please add at least one condition for automated collections');
      return;
    }

    if (formData.collection_type === 'automated' && formData.rules.some(r => !r.value)) {
      toast.error('Please fill in all condition values');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        title: formData.title.trim(),
        description: formData.description,
        handle: formData.handle,
        collection_type: formData.collection_type,
        listing_ids: formData.collection_type === 'manual' ? selectedProducts.map(p => p.id) : [],
        rules: formData.collection_type === 'automated' ? formData.rules : [],
        conditions_match: formData.conditions_match,
        is_visible: formData.is_visible,
        sort_order: formData.sort_order,
        seo_title: formData.seo_title,
        seo_description: formData.seo_description,
      };

      if (isEdit) {
        await axios.put(`${API_URL}/api/collections/${id}`, payload);
        toast.success('Collection updated successfully');
      } else {
        await axios.post(`${API_URL}/api/collections/`, payload);
        toast.success('Collection created successfully');
      }
      navigate('/collections');
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.error(error.response?.data?.detail || 'Failed to save collection');
    } finally {
      setLoading(false);
    }
  };

  const generateHandle = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();
  };

  const handleProductToggle = (product) => {
    const exists = selectedProducts.find(p => p.id === product.id);
    if (exists) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const filteredAvailableProducts = availableProducts.filter(p =>
    p.title.toLowerCase().includes(productSearch.toLowerCase())
  );

  const addCondition = () => {
    setFormData({
      ...formData,
      rules: [
        ...formData.rules,
        { field: 'title', operator: 'contains', value: '' }
      ]
    });
  };

  const removeCondition = (index) => {
    const newRules = formData.rules.filter((_, i) => i !== index);
    setFormData({ ...formData, rules: newRules });
  };

  const updateCondition = (index, key, value) => {
    const newRules = [...formData.rules];
    newRules[index] = { ...newRules[index], [key]: value };
    
    // If field changes, reset operator and value
    if (key === 'field') {
      const fieldType = PRODUCT_FIELDS.find(f => f.value === value)?.type || 'text';
      const defaultOperator = OPERATORS[fieldType][0].value;
      newRules[index] = { field: value, operator: defaultOperator, value: '' };
    }
    
    setFormData({ ...formData, rules: newRules });
  };

  const fetchMatchedProducts = async () => {
    if (!formData.rules.length || formData.rules.some(r => !r.value)) {
      setMatchedProducts([]);
      return;
    }

    try {
      setLoadingMatches(true);
      const response = await axios.get(`${API_URL}/api/listings/`);
      const allProducts = response.data.filter(p => p.status === 'active');
      
      // Client-side filtering based on rules
      const filtered = allProducts.filter(product => {
        const results = formData.rules.map(rule => {
          const fieldValue = product[rule.field] || product.data?.[rule.field];
          
          switch (rule.operator) {
            case 'equals':
              if (Array.isArray(fieldValue)) {
                return fieldValue.includes(rule.value);
              }
              return String(fieldValue).toLowerCase() === String(rule.value).toLowerCase();
            
            case 'contains':
              if (Array.isArray(fieldValue)) {
                return fieldValue.some(v => 
                  String(v).toLowerCase().includes(String(rule.value).toLowerCase())
                );
              }
              return String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
            
            case 'greater_than':
              return parseFloat(fieldValue) > parseFloat(rule.value);
            
            case 'less_than':
              return parseFloat(fieldValue) < parseFloat(rule.value);
            
            default:
              return false;
          }
        });

        return formData.conditions_match === 'all' 
          ? results.every(r => r) 
          : results.some(r => r);
      });

      setMatchedProducts(filtered);
    } catch (error) {
      console.error('Error fetching matched products:', error);
    } finally {
      setLoadingMatches(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/collections')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">{isEdit ? 'Edit collection' : 'Create collection'}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Title & Description */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setFormData({ 
                      ...formData, 
                      title,
                      handle: !isEdit ? generateHandle(title) : formData.handle
                    });
                  }}
                  placeholder="e.g., Summer collection, Under $100, Staff picks"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe this collection..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Collection Type */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-lg font-semibold">Collection type</Label>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    id="manual"
                    checked={formData.collection_type === 'manual'}
                    onChange={() => setFormData({ ...formData, collection_type: 'manual' })}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="manual" className="font-semibold cursor-pointer">Manual</Label>
                    <p className="text-sm text-gray-600">Add products to this collection one by one.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    id="automated"
                    checked={formData.collection_type === 'automated'}
                    onChange={() => setFormData({ ...formData, collection_type: 'automated' })}
                    className="mt-1"
                  />
                  <div>
                    <Label htmlFor="automated" className="font-semibold cursor-pointer">Automated</Label>
                    <p className="text-sm text-gray-600">
                      Existing and future products that match conditions will automatically be added.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products (Manual) */}
          {formData.collection_type === 'manual' && (
            <Card>
              <CardContent className="pt-6">
                <Label className="text-lg font-semibold">Products</Label>

                {selectedProducts.length === 0 ? (
                  <div className="mt-4 text-center py-8 border-2 border-dashed rounded-lg">
                    <p className="text-gray-500">No products added yet</p>
                    <Button variant="link" onClick={() => setIsProductModalOpen(true)}>
                      Search or browse to add products
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search products"
                          className="pl-10"
                          disabled
                        />
                      </div>
                      <Button variant="outline" onClick={() => setIsProductModalOpen(true)}>
                        Browse
                      </Button>
                      <Select disabled>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Sort: Best selling" />
                        </SelectTrigger>
                      </Select>
                    </div>

                    <div className="space-y-0 border rounded-lg divide-y">
                      {selectedProducts.map((product, idx) => (
                        <div key={product.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                          <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                          <ListingImage
                            src={getFirstImage(product.media)}
                            alt={product.title}
                            className="w-12 h-12 object-cover rounded flex-shrink-0"
                            placeholderContent={<span className="text-xs text-gray-400">No image</span>}
                          />
                          <span className="flex-1 font-medium">{product.title}</span>
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeProduct(product.id)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Conditions (Automated) */}
          {formData.collection_type === 'automated' && (
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label className="text-lg font-semibold">Conditions</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    Existing and future products that match the conditions you set will automatically be added to this collection.
                  </p>
                </div>

                {formData.rules.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="font-medium">Products must match:</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="all-conditions"
                          checked={formData.conditions_match === 'all'}
                          onChange={() => setFormData({ ...formData, conditions_match: 'all' })}
                          className="cursor-pointer"
                        />
                        <Label htmlFor="all-conditions" className="cursor-pointer font-normal">all conditions</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="any-condition"
                          checked={formData.conditions_match === 'any'}
                          onChange={() => setFormData({ ...formData, conditions_match: 'any' })}
                          className="cursor-pointer"
                        />
                        <Label htmlFor="any-condition" className="cursor-pointer font-normal">any condition</Label>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {formData.rules.map((rule, index) => {
                        const field = PRODUCT_FIELDS.find(f => f.value === rule.field);
                        const fieldType = field?.type || 'text';
                        const operators = OPERATORS[fieldType] || OPERATORS.text;

                        return (
                          <div key={rule.id || rule.name || `${rule.title || "rule"}-${index}`} className="flex items-center gap-2">
                            <Select
                              value={rule.field}
                              onValueChange={(value) => updateCondition(index, 'field', value)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PRODUCT_FIELDS.map(field => (
                                  <SelectItem key={field.value} value={field.value}>
                                    {field.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select
                              value={rule.operator}
                              onValueChange={(value) => updateCondition(index, 'operator', value)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {operators.map(op => (
                                  <SelectItem key={op.value} value={op.value}>
                                    {op.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Input
                              value={rule.value}
                              onChange={(e) => updateCondition(index, 'value', e.target.value)}
                              placeholder={fieldType === 'number' ? '₹' : 'Enter value...'}
                              type={fieldType === 'number' ? 'number' : 'text'}
                              className="flex-1"
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCondition(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    {formData.rules.some(r => r.field === 'price' || r.field === 'compare_at_price') && (
                      <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-yellow-800">
                          This collection will include all products with at least one variant that matches the price condition.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={addCondition}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add condition
                </Button>

                {/* Matched Products Preview */}
                {formData.rules.length > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-semibold">
                        Products matching conditions ({matchedProducts.length})
                      </Label>
                      {loadingMatches && (
                        <span className="text-xs text-gray-500">Loading...</span>
                      )}
                    </div>

                    {matchedProducts.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4 border rounded-lg">
                        No products match the current conditions
                      </p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {matchedProducts.slice(0, 10).map((product) => (
                          <div key={product.id} className="flex items-center gap-3 p-2 border rounded-lg text-sm">
                            <ListingImage
                              src={getFirstImage(product.media)}
                              alt={product.title}
                              className="w-10 h-10 object-cover rounded flex-shrink-0"
                              showPlaceholder={false}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{product.title}</p>
                              <p className="text-xs text-gray-500">{product.category}</p>
                            </div>
                            <Badge variant="secondary" className="text-xs">Active</Badge>
                          </div>
                        ))}
                        {matchedProducts.length > 10 && (
                          <p className="text-xs text-gray-500 text-center pt-2">
                            +{matchedProducts.length - 10} more products
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Theme Template */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="font-semibold">Theme template</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Assign a template from your current theme to define how the collection is displayed
                </p>
              </div>
              <Select value="default" disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Default collection" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default collection</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* SEO */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-lg font-semibold">Search engine listing</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Add a title and description to see how this collection might appear in a search engine listing
                </p>
              </div>
              <div>
                <Label>Page title</Label>
                <Input
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder={formData.title || "e.g., Summer collection"}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.seo_title.length || 0} of 70 characters used
                </p>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  rows={3}
                  placeholder="Brief description for search engines..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.seo_description.length || 0} of 320 characters used
                </p>
              </div>
              <div>
                <Label>URL handle</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">collections/</span>
                  <Input value={formData.handle} disabled className="bg-gray-50 flex-1" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="font-semibold">Collection availability</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                  />
                  <Label className="cursor-pointer">Visible on storefront</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/collections')} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading || !formData.title} className="flex-1">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Product Selection Modal */}
      <Dialog open={isProductModalOpen} onOpenChange={setIsProductModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add products</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredAvailableProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleProductToggle(product)}
                >
                  <Checkbox
                    checked={!!selectedProducts.find(p => p.id === product.id)}
                    onCheckedChange={() => handleProductToggle(product)}
                  />
                  <ListingImage
                    src={getFirstImage(product.media)}
                    alt={product.title}
                    className="w-12 h-12 object-cover rounded flex-shrink-0"
                    showPlaceholder={false}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{product.title}</p>
                    <p className="text-sm text-gray-500">{product.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setIsProductModalOpen(false)}>Cancel</Button>
            <Button onClick={() => setIsProductModalOpen(false)}>
              Add {selectedProducts.length} product(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CollectionForm;
